import React, { useState, memo, useEffect, useCallback, useRef } from 'react'
import moment from 'moment'
import { DragDropContext } from 'react-beautiful-dnd'
import { withStyles } from '@material-ui/core/styles'
import { useDispatch } from 'react-redux'
import { withRouter } from 'react-router-dom'

import Box from '@material-ui/core/Box'
import Col from 'react-bootstrap/lib/Col'
import Button from 'Components/Form/Button'
import { SearchInput } from 'Components/Header/SearchInput'
import Progress from 'Components/Progress'
import { SelectDashboard } from '../../../../../Components/Form'
import PipelineColumn from './components/PipelineColumn'
import RefusedModalBody from './components/RefusedModalBody'

import {
  showReduxModal,
  hideReduxModal,
  resetReduxModal,
} from 'Reducers/UI/actions'
import { downloadStringAsFile } from 'Utils/Misc'
import { escapeCsvString } from 'shared/csv'
import { useAmbassadorsRequest } from '../gql'
import { filterPipelines } from './utils'

import {
  STATUS_PENDING,
  STATUS_ACTIVE,
  STATUS_PRE_ACCESS,
  STATUS_WAITING_FOR_REVIEW,
  STATUS_BLOCKED,
  BRAND_AMBASSADORS_STATUS_ENUM_MAP,
} from 'shared/constants'
import {
  WITHHOLD_OPTIONS_LABEL_ENUM,
  WITHHOLD_OPTIONS_VALUE_ENUM,
  withholdPayOptions,
} from './options'

import style from './style'

const cache = {}
function AmbassadorsPipelineView({ classes, cityId, match: { url } }) {
  const [pipelines, setPipelines] = useState({
    interested: [],
    booked: [],
    refused: [],
    preAccess: [],
    finalReview: [],
    approved: [],
  })
  const dispatch = useDispatch()

  const oldPipelines = useRef({})
  const {
    progressOfGetAllAmbassadors,
    getAllAmbassadors,
    getAmbassadorCalendarEvents,
    updateAmbassador,
    deleteAmbassador,
  } = useAmbassadorsRequest(cityId)

  const setPipelinesHandler = pipelines => {
    setPipelines(pipelines)
    cache[cityId + 'ambassadors'] = pipelines
  }

  const fetchAmbassadors = async () => {
    if (cache[cityId + 'ambassadors']) {
      setPipelines(cache[cityId + 'ambassadors'])
    } else {
      const [
        { ambassadors: nonBlockedAmbassadors },
        events,
      ] = await Promise.all([
        getAllAmbassadors({
          statuses: ['PENDING', 'WAITING_FOR_REVIEW', 'PRE_ACCESS', 'ACTIVE'],
        }),
        getAmbassadorCalendarEvents(),
      ])
      const nonBlockedPipelines = convertAmbassadorsToPipelines(
        nonBlockedAmbassadors,
        events,
      )
      setPipelinesHandler(nonBlockedPipelines)

      const { ambassadors: blockedAmbassadors } = await getAllAmbassadors(
        {
          statuses: 'BLOCKED',
        },
        { progressName: 'blocked' },
      )

      const blockedPipelines = convertAmbassadorsToPipelines(
        blockedAmbassadors,
        events,
      )

      const allPipelines = {
        ...nonBlockedPipelines,
        refused: blockedPipelines['refused'],
      }
      setPipelinesHandler(allPipelines)
      oldPipelines.current = allPipelines
    }
  }

  useEffect(() => {
    fetchAmbassadors()
  }, [])

  const convertAmbassadorsToPipelines = useCallback((ambassadors, events) => {
    const pipelines = ambassadors.reduce(
      (acc, item) => {
        if (!item) return acc

        item.memberSinceFormated = dateFormater(item.memberSince)

        const index = events.findIndex(
          eventsItem => eventsItem.userId === item.id,
        )

        var updatedItem = ~index ? { ...item, ...events[index] } : item
        if (~index) {
          events.splice(index, 1)
        }

        if (BRAND_AMBASSADORS_STATUS_ENUM_MAP[item.status] === STATUS_BLOCKED) {
          acc.refused.push(updatedItem)
        }

        if (
          BRAND_AMBASSADORS_STATUS_ENUM_MAP[item.status] === STATUS_PRE_ACCESS
        ) {
          acc.preAccess.push(updatedItem)
        }

        if (
          BRAND_AMBASSADORS_STATUS_ENUM_MAP[item.status] ===
          STATUS_WAITING_FOR_REVIEW
        ) {
          acc.finalReview.push(updatedItem)
        }

        if (BRAND_AMBASSADORS_STATUS_ENUM_MAP[item.status] === STATUS_ACTIVE) {
          acc.approved.push(updatedItem)
        }

        if (BRAND_AMBASSADORS_STATUS_ENUM_MAP[item.status] === STATUS_PENDING) {
          if (!~index) {
            acc.interested.push(updatedItem)
          }

          if (~index) {
            acc.booked.push(updatedItem)
          }
        }

        return acc
      },
      {
        interested: [],
        booked: [],
        refused: [],
        preAccess: [],
        finalReview: [],
        approved: [],
      },
    )
    return pipelines
  }, [])

  const onExportData = useCallback(column => {
    const exportHeaders = [
      'Id',
      'First Name',
      'Last Name',
      'Email',
      'Recruitment Channel',
      'Mobile Phone',
      'Rating',
      'Member Since',
      'City',
      'Status',
    ]

    let content = exportHeaders.join(',')
    column.forEach(item => {
      const rowData = {
        'Id': item.id,
        'First Name': item.firstName,
        'Last Name': item.lastName,
        'Recruitment Channel': item.recruitmentChannel,
        'Email': item.email,
        'Mobile Phone': item.mobilePhone,
        'Rating': item.rating,
        'Member Since': item.memberSinceFormated,
        'City': item.city.name,
        'Missions': item.missions && item.missions.totalCount,
        'Status': item.status,
      }
      content +=
        '\n' +
        exportHeaders
          .map(headerName => {
            const value = rowData[headerName]
            return typeof value === 'string' ? escapeCsvString(value) : value
          })
          .join(',')
    })
    downloadStringAsFile(content, `city_${cityId}.csv`, 'text/csv')
  })

  const transfer = (from, to, item) => {
    const sourceClone = [...pipelines[from]]
    const destinationClone = [...pipelines[to]]
    sourceClone.splice(item.index, 1)
    destinationClone.unshift(item)
    setPipelinesHandler({
      ...pipelines,
      [from]: sourceClone,
      [to]: destinationClone,
    })
  }

  const reset = () => {
    setPipelinesHandler(oldPipelines.current)
  }

  const handlerDragEnd = useCallback(
    async result => {
      const { source, destination, draggableId: userId } = result

      if (validateStatusMovement(source, destination)) {
        oldPipelines.current = pipelines

        const { itemData, index } = pipelines[source.droppableId].reduce(
          (acc, item, index) => {
            if (item.id == userId) return { itemData: item, index }
            return acc
          },
          {
            itemData: {},
            index: 0,
          },
        )

        const item = {
          ...itemData,
          index,
          from: source.droppableId,
          to: destination.droppableId,
        }

        if (destination.droppableId === 'refused') {
          handleRefusedModalOpen(item)
        } else if (destination.droppableId === 'preAccess') {
          transfer(source.droppableId, 'preAccess', {
            ...item,
            status: 'PRE_ACCESS',
          })
          try {
            await updateAmbassador(item.id, {
              statusV2: STATUS_PRE_ACCESS,
            })
          } catch (error) {
            reset()
          }
        } else if (destination.droppableId === 'approved') {
          handleApprovedModalOpen(item, false)
        }
      }
    },
    [pipelines],
  )

  const handlerSearch = useCallback(
    searchValue => {
      if (!searchValue || !searchValue.length)
        return setPipelines(cache[cityId + 'ambassadors'])

      setPipelines(
        Object.entries(cache[cityId + 'ambassadors']).reduce(
          (acc, [key, value]) => {
            acc[key] = value.filter(item =>
              `${item.lastName} ${item.firstName}`
                .toLowerCase()
                .includes(searchValue.toLowerCase()),
            )
            return acc
          },
          {
            interested: [],
            booked: [],
            refused: [],
            preAccess: [],
            finalReview: [],
            approved: [],
          },
        ),
      )
    },
    [pipelines],
  )

  const handleRefusedModalOpen = useCallback(
    async item => {
      const onSubmit = async data => {
        transfer(item.from, 'refused', {
          ...item,
          status: 'BLOCKED',
        })
        try {
          await updateAmbassador(item.id, data)
        } catch (error) {
          reset()
        }
        onCancel()
      }

      const onCancel = () => {
        dispatch(hideReduxModal())
        setTimeout(() => dispatch(resetReduxModal()), 100)
      }

      dispatch(
        showReduxModal({
          title: 'Blocked reason',
          hideButtons: true,
          children: (
            <RefusedModalBody onSubmit={onSubmit} onCancel={onCancel} />
          ),
        }),
      )
    },
    [pipelines],
  )

  const handleDelete = useCallback(
    async item => {
      dispatch(
        showReduxModal({
          title: 'Confirmation needed',
          onConfirm: async () => {
            const sourceClone = [...pipelines[item.columnName]]
            const index = sourceClone.findIndex(
              sourceItem => sourceItem.id === item.id,
            )
            sourceClone.splice(index, 1)
            setPipelinesHandler({
              ...pipelines,
              [item.columnName]: sourceClone,
            })
            try {
              await deleteAmbassador(item.id)
            } catch (error) {
              reset()
            }
          },
          children: (
            <Box
              display="flex"
              flexWrap="wrap"
              justifyContent="center"
              mt={3}
              mb={3}
            >
              Are you sure you want to delete {item.firstName} {item.lastName}?
            </Box>
          ),
        }),
      )
    },
    [pipelines],
  )

  const handleApprovedModalOpen = useCallback(
    (item, isHedeActiveButtons) => {
      const handleApprove = async () => {
        transfer('finalReview', 'approved', {
          ...item,
          status: 'ACTIVE',
        })
        try {
          await updateAmbassador(item.id, {
            statusV2: STATUS_ACTIVE,
          })
        } catch (error) {
          reset()
        }
        handleCancel()
      }

      const handleCancel = () => {
        dispatch(hideReduxModal())
        setTimeout(() => dispatch(resetReduxModal()), 100)
      }

      const handleRefuse = async () => {
        handleRefusedModalOpen(item)
      }

      dispatch(
        showReduxModal({
          title: (
            <div>
              <span>Document review</span>
              <br />
              <span>
                {item.lastName} {item.firstName}
              </span>
            </div>
          ),
          hideButtons: true,
          children: (
            <Box mt={3} mb={3}>
              <Box display="flex" flexWrap="wrap" mt={3} mb={3}>
                {item.providedPersonalDocuments.edges.map(
                  ({ node: item }, index) => (
                    <Box
                      key={item.documentTypeName + index}
                      width="40%"
                      m={3}
                      textAlign="center"
                    >
                      <span>{item.documentTypeName}</span>
                      <br />
                      <span>
                        <a target="_blank" href={item.url}>
                          Click here
                        </a>
                      </span>
                    </Box>
                  ),
                )}
              </Box>
              <Box
                display="flex"
                flexWrap="wrap"
                justifyContent="center"
                mt={3}
                mb={3}
              >
                {!isHedeActiveButtons && (
                  <Box mx={1}>
                    <Button value="Refuse" onClick={handleRefuse} />
                  </Box>
                )}
                <Box mx={1}>
                  <Button value="Cancel" onClick={handleCancel} />
                </Box>
                {!isHedeActiveButtons && (
                  <Box mx={1}>
                    <Button value="Approve" onClick={handleApprove} />
                  </Box>
                )}
              </Box>
            </Box>
          ),
        }),
      )
    },
    [pipelines],
  )

  const onWithHoldChange = useCallback(
    (event, key, value) => {
      if (value === WITHHOLD_OPTIONS_VALUE_ENUM.ALL) {
        return setPipelines(cache[cityId + 'ambassadors'])
      } else if (value === WITHHOLD_OPTIONS_VALUE_ENUM.ON) {
        const filteredPipelines = filterPipelines(
          cache[cityId + 'ambassadors'],
          item => item.withholdPay,
        )
        setPipelines(filteredPipelines)
      } else if (value === WITHHOLD_OPTIONS_VALUE_ENUM.OFF) {
        const filteredPipelines = filterPipelines(
          cache[cityId + 'ambassadors'],
          item => !item.withholdPay,
        )
        setPipelines(filteredPipelines)
      }
    },
    [pipelines],
  )

  return (
    <div className={classes.wrap}>
      <div className={classes.wrapForm}>
        <Col xs={4}>
          <SearchInput placeholder="Search" searchHandler={handlerSearch} />
        </Col>
        <Col xs={3}>
          <SelectDashboard
            label="Withhold"
            options={withholdPayOptions}
            value={WITHHOLD_OPTIONS_LABEL_ENUM.ALL}
            onChange={onWithHoldChange}
          />
        </Col>
      </div>
      <DragDropContext onDragEnd={handlerDragEnd}>
        <div className={classes.view}>
          {Object.entries(pipelines).map(([key, value], index) => (
            <PipelineColumn
              key={key + index}
              items={value}
              progressOfGetBlockedAmbassadors={
                progressOfGetAllAmbassadors.blocked
              }
              onExportData={onExportData}
              onDelete={handleDelete}
              onApprovedModalOpen={handleApprovedModalOpen}
              onDocumentsModalOpen={handleApprovedModalOpen}
              name={key}
              url={url}
            />
          ))}
        </div>
      </DragDropContext>
      {progressOfGetAllAmbassadors.isLoading && (
        <Progress label={progressOfGetAllAmbassadors.label} />
      )}
    </div>
  )
}

const validateStatusMovement = (from, to) => {
  if (!to) {
    return false
  }

  if (from.droppableId === to.droppableId) {
    return false
  }

  if (to.droppableId === 'refused') {
    return true
  }

  if (from.droppableId === 'booked' && to.droppableId === 'preAccess') {
    return true
  }

  if (from.droppableId === 'interested' && to.droppableId === 'preAccess') {
    return true
  }

  if (from.droppableId === 'finalReview' && to.droppableId === 'approved') {
    return true
  }
}

export const dateFormater = memberSince => {
  return moment(memberSince).format('D-MMM-YYYY')
}

export default memo(withStyles(style)(withRouter(AmbassadorsPipelineView)))



// WEBPACK FOOTER //
// ./src/Containers/City/Page/Ambasadors/AmbassadorsPipelineView/index.js